import { createSlice } from '@reduxjs/toolkit'
import { adminOrganizationApi } from '../api/admin-organization-api'
import { adminOrganizationUsersApi } from '../api/admin-organization-users-api'

import slicesNotifications from './notifications'

const initialState = {
    status: {
        initialized: false,
        processing: false,
        has_error: false,
        error_message: null
    },
    listView: {
        items: []
    },
    infoView: {
        item: null,
        account_permissions: []
    },
    accountPermissionUsers: {
        item: []
    }
}

const slice = createSlice({
    name: 'admin-organization-users',
    initialState,
    reducers: {
        initializeRequest(state, action) {
            state.status = {
                initialized: false,
                processing: true,
                has_error: false,
                error_message: null
            }
        },

        initializeFailed(state, action) {
            state.status = {
                initialized: false,
                processing: false,
                has_error: true,
                error_message: action.payload.errorMessage
            }
        },

        updateListView(state, action) {
            const { body } = action.payload
            const { listView, infoView } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.listView = {
                items: listView.items || []
            }

            if (infoView) {
                state.infoView = {
                    item: infoView.item || null,
                    account_permissions: infoView.account_permissions || []
                }
            }
        },

        updateInfoView(state, action) {
            const { body } = action.payload
            const { listView, infoView } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.listView = {
                items: listView.items || []
            }

            state.infoView = {
                item: infoView.item || null,
                account_permissions: infoView.account_permissions || []
            }
        },

        updateAccountPermissionUsers(state, action) {
            const { body } = action.payload
            const { accountPermissionUsers } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.accountPermissionUsers = {
                items: accountPermissionUsers.items || []
            }
        }
    }
})

export const { reducer } = slice

export const loadList = (organization_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationUsersApi.list(organization_id)

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(
            slicesNotifications.addApiError(data, 'Loading Organization Users')
        )
    } else {
        dispatch(slice.actions.updateListView(data))
    }
}

export const infoRequest = (organization_id, user_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationUsersApi.info(organization_id, user_id)

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(
            slicesNotifications.addApiError(data, 'Organization User Details')
        )
    } else {
        dispatch(slice.actions.updateListView(data))
    }

    return data
}

export const createRequest =
    (
        organization_id,
        user_permission_type,
        usr_first_name,
        usr_last_name,
        usr_email,
        organization_account_ids,
        mail_text_plain
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationUsersApi.create(
            organization_id,
            user_permission_type,
            usr_first_name,
            usr_last_name,
            usr_email,
            organization_account_ids,
            mail_text_plain
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Creating Organization User'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Creating Organization User',
                    'User created successfully'
                )
            )
        }

        return data
    }

export const updateRequest =
    (
        organization_id,
        user_id,
        user_permission_type,
        organization_account_ids
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationUsersApi.update(
            organization_id,
            user_id,
            user_permission_type,
            organization_account_ids
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Updating Organization User'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Updating Organization User',
                    'User updated successfully'
                )
            )
        }

        return data
    }

export const deleteRequest = (organization_id, user_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationUsersApi.delete(
        organization_id,
        user_id
    )

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(
            slicesNotifications.addApiError(data, 'Organization User Removal')
        )
    } else {
        dispatch(slice.actions.updateListView(data))
        dispatch(
            slicesNotifications.addApiSuccess(
                data,
                'Organization User Removal',
                'User removed successfully'
            )
        )
    }

    return data
}

export const infoAccountPermissionUsers =
    (organization_id, organization_account_id) => async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationUsersApi.infoAccountPermissionUsers(
            organization_id,
            organization_account_id
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Updating Organization User'
                )
            )
        } else {
            dispatch(slice.actions.updateAccountPermissionUsers(data))
        }

        return data
    }

export const inviteInfoRequest = (invite_ref_id) => async (dispatch) => {
    const data = await adminOrganizationUsersApi.inviteInfo(invite_ref_id)

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(slicesNotifications.addApiError(data, 'Invitation'))
    } else {
    }

    return data
}

export const inviteJoinRequest = (invite_ref_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationUsersApi.inviteJoin(invite_ref_id)

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(slicesNotifications.addApiError(data, 'Invitation'))
    } else {
    }

    return data
}

export const inviteApproveRequest = (invite_ref_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationUsersApi.inviteApprove(invite_ref_id)

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(slicesNotifications.addApiError(data, 'Invitation'))
    } else {
        dispatch(
            slicesNotifications.addApiSuccess(
                data,
                'Organization Invitation',
                'Invitation Approved'
            )
        )
    }

    return data
}

export const inviteRejectRequest = (invite_ref_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationUsersApi.inviteReject(invite_ref_id)

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(slicesNotifications.addApiError(data, 'Invitation'))
    } else {
        dispatch(
            slicesNotifications.addApiSuccess(
                data,
                'Organization Invitation',
                'Invitation Rejected'
            )
        )
    }

    return data
}

export default {
    inviteInfoRequest,
    inviteJoinRequest,
    inviteApproveRequest,
    inviteRejectRequest
}
